import { applicationInsights } from '../../../../../../ApplicationInsights';
import { BfiDataGrid, Column, formatDate } from '@apply/ui';
import { GridSettingType } from '@apply/constants';
import { useBrokerDetailsStore } from '../../../../state/brokerDetailsStore';
import { useGetUserAgreementsQuery, useCreateUserAgreementPdfMutation, downloadAgreementPdf } from '../../../../queries/brokerDetailsQueries';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHourglassHalf, faFileExcel, faArrowCircleDown } from '@fortawesome/free-solid-svg-icons';
import { handleAdminError, handleAdminSuccess } from '../../../../../../common/SharedFunctions';
import { Button, ButtonTheme } from '@apply/components';

export const UserAgreementsTable = () => {
    const brokerId = useBrokerDetailsStore((s) => s.brokerId);
    const { data: rows, refetch } = useGetUserAgreementsQuery(brokerId);
    const createPdfMutation = useCreateUserAgreementPdfMutation();

    const onDateSignedRender = (e: unknown) => {
        if (!e || typeof e !== 'object' || !('value' in e) || !(e.value instanceof Date)) {
            return null;
        }
        return formatDate(e.value);
    };

    const onVersionRender = (e: unknown) => {
        if (!e || typeof e !== 'object' || !('data' in e)) {
            return null;
        }
        const data = e.data as { templateVersion?: { name?: string } };
        return data.templateVersion?.name ?? null;
    };

    const onFileRender = (e: unknown) => {
        if (!e || typeof e !== 'object' || !('data' in e)) {
            return null;
        }
        const rowData = e.data as {
            id: number;
            signedUserAgreementGeneratingUrl?: string;
            signedUserAgreementDownloadToken?: string;
        };

        if (rowData.signedUserAgreementGeneratingUrl) {
            return (
                <span
                    className='signed-user-agreement-message'
                    onClick={() => refetch()}
                >
                    <FontAwesomeIcon
                        title='User Agreement PDF is generating...click to refresh status.'
                        icon={faHourglassHalf}
                    />
                </span>
            );
        }

        if (rowData.signedUserAgreementDownloadToken) {
            return (
                <Button
                    id='downloadButton'
                    theme={ButtonTheme.Secondary}
                    handleClick={() => downloadAgreementPdf(rowData.signedUserAgreementDownloadToken!)}
                >
                    <FontAwesomeIcon icon={faArrowCircleDown} /> Download
                </Button>
            );
        }

        return (
            <Button
                id='createPdfButton'
                theme={ButtonTheme.Secondary}
                handleClick={() => {
                    createPdfMutation.mutate(rowData.id, {
                        onSuccess: () => {
                            handleAdminSuccess(
                                'The system was able to proceed with PDF generation.  It should be available shortly.',
                            );
                        },
                        onError: (error: Error) => {
                            handleAdminError(
                                error,
                                'The system was not able to process this PDF. Please contact support.'
                            );
                        },
                    });
                }}
            >
                <FontAwesomeIcon icon={faFileExcel} /> Create PDF
            </Button>
        );
    };

    return (
        <BfiDataGrid
            dataSource={rows ?? []}
            keyExpr='id'
            showBorders={true}
            showRowLines={false}
            rowAlternationEnabled={true}
            remoteOperations={false}
            allowColumnReordering={true}
            allowColumnResizing={true}
            noDataText='No User Agreements available'
            defaultPageSize={25}
            allowedPageSizes={[15, 25, 50]}
            applicationInsights={applicationInsights}
            gridType={GridSettingType.UserAgreementsTable}
        >
            <Column
                dataField='userAgreementType'
                caption='Agreement Type'
                dataType='string'
                alignment={'left'}
            />
            <Column
                dataField='dateSigned'
                caption='Date Signed'
                cellRender={onDateSignedRender}
                width={120}
                alignment={'left'}
            />
            <Column
                caption='Version'
                dataType='string'
                cellRender={onVersionRender}
                alignment={'left'}
            />
            <Column
                dataField='fileLink'
                caption='User Agreement File'
                cellRender={onFileRender}
                alignment={'left'}
            />
        </BfiDataGrid>
    );
};
